import { useState, useCallback, useEffect } from 'react';
import { getUsersByOrganizationAndRole, getUsersByOrganizationAndRolePaginated, getUsersByOrganization } from '../helpers/utils';

var [data, error, loading] = [null, false, false]

export const Fetch = async(url, options) =>{
    loading = true;
    let result = "";

    //if url is not provided
    if (!url) {
        return null;
    }
    var response= ""
    try{
     response = await fetch(url, options);
    }catch(err){}
    if (response.ok) {
         result = await response.json();
        if (result.error) {
            console.log(result.error)
            error = true
        }

        data = result;
        error = false;
    }else{
        error = true;
    }
    loading = false;
    return {data, error, loading};
}

/**
 * Custom hook to fetch Auth0 users by organization and role
 * @param {string} organizationName - The organization name to filter by
 * @param {string} role - The role to filter by (default: "subscriber")
 * @returns {Object} Object containing users, loading state, error state, and refetch function
 */
export const useUsersByOrganizationAndRole = (organizationName, role = "subscriber") => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchUsers = useCallback(async () => {
        if (!organizationName) {
            setUsers([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const fetchedUsers = await getUsersByOrganizationAndRole(organizationName, role);
            setUsers(fetchedUsers);
        } catch (err) {
            setError(err.message);
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    }, [organizationName, role]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    return {
        users,
        loading,
        error,
        refetch: fetchUsers
    };
};

/**
 * Custom hook to fetch Auth0 users by organization and role with pagination
 * @param {string} organizationName - The organization name to filter by
 * @param {string} role - The role to filter by (default: "subscriber")
 * @param {number} page - Page number (default: 0)
 * @param {number} perPage - Number of users per page (default: 100)
 * @returns {Object} Object containing users, pagination info, loading state, error state, and refetch function
 */
export const useUsersByOrganizationAndRolePaginated = (
    organizationName, 
    role = "subscriber", 
    page = 0, 
    perPage = 100
) => {
    const [users, setUsers] = useState([]);
    const [pagination, setPagination] = useState({
        total: 0,
        start: 0,
        limit: perPage,
        length: 0
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchUsers = useCallback(async () => {
        if (!organizationName) {
            setUsers([]);
            setPagination({
                total: 0,
                start: 0,
                limit: perPage,
                length: 0
            });
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await getUsersByOrganizationAndRolePaginated(organizationName, role, page, perPage);
            setUsers(result.users);
            setPagination({
                total: result.total,
                start: result.start,
                limit: result.limit,
                length: result.length
            });
        } catch (err) {
            setError(err.message);
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    }, [organizationName, role, page, perPage]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    return {
        users,
        pagination,
        loading,
        error,
        refetch: fetchUsers
    };
};

/**
 * Custom hook to fetch all Auth0 users for a specific organization
 * @param {string} organizationName - The organization name to filter by
 * @returns {Object} Object containing users, loading state, error state, and refetch function
 */
export const useUsersByOrganization = (organizationName) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchUsers = useCallback(async () => {
        if (!organizationName) {
            setUsers([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const fetchedUsers = await getUsersByOrganization(organizationName);
            setUsers(fetchedUsers);
        } catch (err) {
            setError(err.message);
            console.error('Error fetching users by organization:', err);
        } finally {
            setLoading(false);
        }
    }, [organizationName]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    return {
        users,
        loading,
        error,
        refetch: fetchUsers
    };
};